// Profile switcher in the top bar. Shows the current profile's avatar + name,
// opens a dropdown listing all profiles grouped by SELF vs others. Switching
// profile triggers PatientContext.switchProfile which is the trigger for
// patient-scoped cache invalidation across the portal.
//
// v2 (see PATIENT_PORTAL_PLAN §3.6) will collapse same-PersonIdentity profiles
// into a parent row with brand chips. The grouping shape used here already
// accommodates that visual structure.

import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { usePatient } from '../context/PatientContext'
import { logoutHandlerBusiness } from 'src/business/helpers/logoutHandlerBusiness'

const Button = styled.button`
  appearance: none;
  border: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
  background: ${(p) => p.theme?.color?.bgRaised || '#fff'};
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 12px 6px 6px;
  border-radius: 999px;
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  color: ${(p) => p.theme?.color?.ink || '#1F2937'};
  transition: all 200ms ease-out;
  &:hover {
    background: ${(p) => p.theme?.color?.bgSubtle || '#F4F0E9'};
  }
`

const Avatar = styled.span`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${(p) => p.$color || '#1F3A5F'};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.05em;
`

const NameLabel = styled.span`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.1;
  max-width: 180px;
`

const Caret = styled.span`
  margin-left: 4px;
  font-size: 12px;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
`

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 320px;
  /* Cap the dropdown height so a user with many profiles (one per clinic
     brand × multi-family setups) can still see "Add a family member" /
     "Manage profiles" footer actions. The list scrolls inside; the footer
     stays sticky. */
  max-height: min(72vh, 560px);
  display: flex;
  flex-direction: column;
  background: ${(p) => p.theme?.color?.bgRaised || '#fff'};
  border: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
  border-radius: 16px;
  box-shadow: ${(p) => p.theme?.shadow?.raised || '0 4px 16px rgba(31,41,55,0.08)'};
  padding: 8px;
  z-index: ${(p) => p.theme?.z?.modal || 60};
`

const DropdownScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  /* Soft scrollbar to match the calm clinical theme rather than the OS default. */
  scrollbar-width: thin;
  scrollbar-color: ${(p) => p.theme?.color?.borderStrong || '#D5CCBC'} transparent;
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${(p) => p.theme?.color?.borderStrong || '#D5CCBC'};
    border-radius: 999px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`

const DropdownFooter = styled.div`
  flex-shrink: 0;
`

const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
  padding: 8px 12px 4px;
`

const ProfileRow = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 12px;
  background: ${(p) => (p.$active ? p.theme?.color?.primarySoft || '#E8EEF6' : 'transparent')};
  border: none;
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  text-align: left;
  &:hover {
    background: ${(p) => p.theme?.color?.bgSubtle || '#F4F0E9'};
  }
`

const RowText = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`
const RowName = styled.span`
  font-weight: 600;
  color: ${(p) => p.theme?.color?.ink || '#1F2937'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`
const RowMeta = styled.span`
  font-size: 12px;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
`

// Clinic-assigned patient number — shown as a tiny pill below the brand line
// so the patient can read it off without digging into Personal details.
// Hidden when the clinic doesn't assign one (patientNumber blank).
const PatientNumberPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${(p) => p.theme?.color?.bgSubtle || '#F4F0E9'};
  color: ${(p) => p.theme?.color?.inkSoft || '#4B5563'};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  width: fit-content;
  &::before {
    content: '#';
    color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
  }
`

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
  margin: 8px 0;
`

const FootAction = styled.button`
  width: 100%;
  text-align: left;
  background: transparent;
  border: none;
  padding: 10px 12px;
  font-family: inherit;
  font-size: 14px;
  color: ${(p) => p.$danger ? (p.theme?.color?.urgent || '#C24D49') : (p.theme?.color?.primary || '#1F3A5F')};
  font-weight: 600;
  cursor: pointer;
  border-radius: 12px;
  &:hover {
    background: ${(p) => p.$danger ? (p.theme?.color?.urgentSoft || '#FBEAE9') : (p.theme?.color?.bgSubtle || '#F4F0E9')};
  }
`

const Container = styled.div`
  position: relative;
`

// Derives the two-line label for a profile row.
//
//   - SELF rows always read "You — <brand>" as the primary line and the
//     brand alone as the secondary. The patient knows their own name; the
//     differentiator across sibling SELF rows (the same person registered
//     at multiple clinics) is the brand.
//   - Non-SELF rows show the relative's actual name as the primary line and
//     "<Relation> · <brand>" as the secondary. Names come from PatientInfo
//     (clinic-owned), so they're whatever the clinic recorded — Hindi
//     script, nicknames, typos and all. The relation is auto-seeded by name
//     fuzzy-match at link discovery and is user-editable in profile settings.
//
// Defensive against legacy data:
//   - Old (pre-PatientInfo-migration) mock-format displayName "You — Brand"
//     gets stripped of the suffix before re-adding from brandName, so old +
//     new rows render uniformly.
//   - Brand-less rows shouldn't reach the frontend any more (the listing
//     filters them server-side), but if one slips through the secondary
//     line collapses gracefully.
function profileLabels(p) {
  if (!p) return { primary: 'Profile', secondary: '' }
  const relMap = {
    PARENT: 'Parent',
    CHILD: 'Child',
    SPOUSE: 'Spouse',
    SIBLING: 'Sibling',
    FRIEND: 'Friend',
    SELF: 'Self',
    OTHER: 'Family',
  }
  const relLabel = relMap[p.relation] || 'Family'
  const brand = (p.brandName || '').trim()
  const rawName = (p.displayName || '').trim()
  const baseName = rawName.includes(' — ') ? rawName.split(' — ')[0].trim() : rawName

  if (p.relation === 'SELF') {
    return {
      primary: brand ? `You — ${brand}` : 'You',
      secondary: brand,
    }
  }

  return {
    primary: baseName || relLabel,
    secondary: brand ? `${relLabel} · ${brand}` : relLabel,
  }
}

export default function PatientProfileSwitcher() {
  const { currentProfile, groupedProfiles, switchProfile } = usePatient()
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  const navigate = useNavigate()

  useEffect(() => {
    function handleClick(e) {
      if (!ref.current || ref.current.contains(e.target)) return
      setOpen(false)
    }
    if (open) document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  if (!currentProfile) return null

  function handleSelect(id) {
    switchProfile(id)
    setOpen(false)
  }

  return (
    <Container ref={ref}>
      <Button onClick={() => setOpen((v) => !v)} aria-haspopup="listbox" aria-expanded={open}>
        <Avatar $color={currentProfile.avatarColor}>{currentProfile.avatarInitials}</Avatar>
        <NameLabel>
          {(() => {
            const { primary, secondary } = profileLabels(currentProfile)
            return (
              <>
                <span style={{ fontWeight: 600 }}>{primary}</span>
                {secondary ? (
                  <span style={{ fontSize: 12, color: '#6B7280' }}>{secondary}</span>
                ) : null}
              </>
            )
          })()}
        </NameLabel>
        <Caret>▾</Caret>
      </Button>

      {open && (
        <Dropdown role="listbox">
          <DropdownScroll>
          {groupedProfiles.self.length > 0 && (
            <>
              <SectionLabel>You</SectionLabel>
              {groupedProfiles.self.map((p) => {
                const { primary, secondary } = profileLabels(p)
                return (
                  <ProfileRow
                    key={p.id}
                    $active={p.id === currentProfile.id}
                    onClick={() => handleSelect(p.id)}
                  >
                    <Avatar $color={p.avatarColor}>{p.avatarInitials}</Avatar>
                    <RowText>
                      <RowName>{primary}</RowName>
                      {secondary ? <RowMeta>{secondary}</RowMeta> : null}
                      {p.patientNumber ? (
                        <PatientNumberPill>{p.patientNumber}</PatientNumberPill>
                      ) : null}
                    </RowText>
                    {p.id === currentProfile.id && <span aria-hidden>✓</span>}
                  </ProfileRow>
                )
              })}
            </>
          )}

          {groupedProfiles.others.length > 0 && (
            <>
              <SectionLabel>Family</SectionLabel>
              {groupedProfiles.others.map((p) => {
                const { primary, secondary } = profileLabels(p)
                return (
                  <ProfileRow
                    key={p.id}
                    $active={p.id === currentProfile.id}
                    onClick={() => handleSelect(p.id)}
                  >
                    <Avatar $color={p.avatarColor}>{p.avatarInitials}</Avatar>
                    <RowText>
                      <RowName>{primary}</RowName>
                      {secondary ? <RowMeta>{secondary}</RowMeta> : null}
                      {p.patientNumber ? (
                        <PatientNumberPill>{p.patientNumber}</PatientNumberPill>
                      ) : null}
                    </RowText>
                    {p.id === currentProfile.id && <span aria-hidden>✓</span>}
                  </ProfileRow>
                )
              })}
            </>
          )}

          </DropdownScroll>

          <DropdownFooter>
            <Divider />
            <FootAction
              onClick={() => {
                setOpen(false)
                navigate('/patient-home/profile/family')
              }}
            >
              + Add a family member
            </FootAction>
            <FootAction
              onClick={() => {
                setOpen(false)
                navigate('/patient-home/profile/personal')
              }}
            >
              Manage profiles
            </FootAction>
            <Divider />
            <FootAction
              $danger
              onClick={() => {
                setOpen(false)
                logoutHandlerBusiness()
                navigate('/')
              }}
            >
              Sign out
            </FootAction>
          </DropdownFooter>
        </Dropdown>
      )}
    </Container>
  )
}
